import type { i18n as I18n } from 'i18next';
import type { TDocumentDefinitions } from 'pdfmake/interfaces.js';

/**
 * Geçici parolanın hazır mesajı ve giriş kâğıdı (ortak 2026-10-30 madde 9; ADR 0042 §23): e-posta
 * servisi yok, parola e-postayla gönderilmez. Ofis mesajı kopyalayıp telefonla ya da WhatsApp ile
 * iletir ya da kâğıdı yazdırıp verir. Metin arayüz dilinden bağımsız iki dildedir: İngilizce
 * üstte, Türkçe altta (gemide dil karışık; sahibin isteği "çok basit olmalı").
 */
export interface LoginInfo {
  name: string;
  email: string;
  password: string;
  /** Ofis giriş adresi: web uygulamasının kendi kökü (`officeOrigin`); sabit yazılmaz. */
  officeUrl: string;
  /**
   * `false`: kişi devre dışı (Create login onu etkinleştirmez; ADR 0150 §5), "girişiniz hazır"
   * satırı yazılmaz. Verilmezse hazır.
   */
  ready?: boolean;
}

const LANGUAGES = ['en', 'tr'] as const;

/** Bir dildeki satırlar; değerler kaçışsız (düz metin, HTML değil). */
function lines(i18n: I18n, language: (typeof LANGUAGES)[number], info: LoginInfo): string[] {
  const t = i18n.getFixedT(language);
  const k = (key: string, options: Record<string, unknown> = {}) =>
    String(
      t(`setup.users.temp.message.${key}`, {
        ...options,
        interpolation: { escapeValue: false },
      }),
    );
  return [
    k('hello', { name: info.name }),
    ...(info.ready === false ? [] : [k('ready')]),
    k('office', { url: info.officeUrl }),
    k('ship'),
    k('email', { email: info.email }),
    k('password', { password: info.password }),
    k('change'),
  ];
}

/** Panoya giden metin: İngilizce, boş satır, Türkçe. */
export function loginMessage(i18n: I18n, info: LoginInfo): string {
  return LANGUAGES.map((language) => lines(i18n, language, info).join('\n')).join('\n\n');
}

/** Ofis giriş adresi: tarayıcıda uygulamanın kendi kökü; yoksa boş. */
export function officeOrigin(
  scope: { location?: { origin?: string } } | undefined = typeof window === 'undefined'
    ? undefined
    : window,
): string {
  return scope?.location?.origin ?? '';
}

/** Tek sayfalık giriş kâğıdı (pdfmake; kart PDF'leriyle aynı yol, internetsiz). */
export function loginSheetPdf(i18n: I18n, info: LoginInfo): TDocumentDefinitions {
  const block = (language: (typeof LANGUAGES)[number]) => {
    const [hello, ...rest] = lines(i18n, language, info);
    const title = String(i18n.getFixedT(language)('setup.users.temp.sheetTitle'));
    return [
      {
        text: title,
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 12] as [number, number, number, number],
      },
      { text: hello ?? '', fontSize: 13, margin: [0, 0, 0, 8] as [number, number, number, number] },
      ...rest.map((text) => ({
        text,
        fontSize: 13,
        margin: [0, 0, 0, 6] as [number, number, number, number],
      })),
    ];
  };
  return {
    pageSize: 'A4',
    pageMargins: [56, 56, 56, 56],
    info: { title: 'Felixmate login' },
    content: [
      ...block('en'),
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 483, y2: 0, lineWidth: 0.5 }],
        margin: [0, 18, 0, 18],
      },
      ...block('tr'),
    ],
  };
}
